import { Container } from "@mui/material";
import "./Consumptions.scss";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ConsumptionsHeader from "./ConsumptionsHeader/ConsumptionsHeader";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import ConsumptionsPeriod from "./ConsumptionsPeriod/ConsumptionsPeriod";
import ConsumptionsDonwload from "./ConsumptionsDownload/ConsumptionsDownload";
import {
  ConsumptionFormatted,
  ConsumptionsPeriodInformation,
  FormattedComparativeConsumptions,
  HourConsumptionFormatted,
  SelectedDataComparativeConsumptions,
  SelectedDataPeriodConsumptions,
  SelectedDownloadData,
  SelectedTabConsumptions,
} from "../../core/models/consumptions/consumptions.model";
import { SearchParams } from "../../core/models/consumptionsBillsSearchBar/consumptionsBillsSearchBar.model";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import ConsumptionsComparative, {
  CompareState,
  DEFAULT_SELECTED_DATA_PERIOD_COMPARATIVE,
  DEFAULT_YEARS_TO_COMPARE_CONSUMPTION,
} from "./ConsumptionsComparative/ConsumptionsComparative";
import { ManagementUpdateCup } from "../../core/models/cupsCifSelector/cupsCifSelector.model";
import { initialSelectedDowloadData } from "../../utils/components/consumptions/consumptions.util";
import ConsumptionsHourly from "./ConsumptionsHourly/ConsumptionsHourly";
import useAgentData from "../../core/hooks/data/useAgentData";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";

export const Consumptions = () => {
  const { ownerData, userData } = useContext(TotalEnergiesContext);
  const { loadingState: agentDataLoadingState } = useAgentData();
  const [consumptionsListPeriod, setConsumptionsListPeriod] = useState<
    ConsumptionFormatted[]
  >([]);
  const [scrollPosition, setScrollPosition] = useState<number | null>(null);
  const tableElementRef = useRef<HTMLDivElement>(null);
  const [consumptionsListComparative, setConsumptionsListComparative] =
    useState<FormattedComparativeConsumptions[]>([]);
  const [consumptionsPeriodInformation, setConsumptionsPeriodInformation] =
    useState<ConsumptionsPeriodInformation>({
      dateFrom: "",
      dateTo: "",
      totalActiveEnergy: "",
      totalConsumedEnergy: "",
    });
  const [searchPeriodParams, setSearchPeriodParams] = useState<SearchParams>({
    dateFrom: null,
    dateTo: null,
    ownerDataCup: initialOwnerDataCup,
  });
  const [selectedDataPeriodPeriod, setSelectedDataPeriodPeriod] =
    useState<SelectedDataPeriodConsumptions>({
      consumption: null,
      positionX: null,
      elements: [],
    });

  const [selectedDataPeriodHourly, setSelectedDataPeriodHourly] =
    useState<SelectedDataPeriodConsumptions>({
      consumption: null,
      positionX: null,
      elements: [],
    });
  const [selectedDataPeriodComparative, setSelectedDataPeriodComparative] =
    useState<SelectedDataComparativeConsumptions>(
      DEFAULT_SELECTED_DATA_PERIOD_COMPARATIVE
    );
  const [selectedTab, setSelectedTab] =
    useState<SelectedTabConsumptions>("download");
  const [selectedDownloadCups, setSelectedDownloadCups] = useState<
    ManagementUpdateCup[]
  >([]);
  const [selectedDownloadData, setSelectedDownloadData] =
    useState<SelectedDownloadData>(initialSelectedDowloadData);

  const [hourlyConsumptionsList, setHourlyConsumptionsList] = useState<
    HourConsumptionFormatted[]
  >([]);

  const [comparisonState, setComparisonState] = useState<CompareState>({
    selectedYears: DEFAULT_YEARS_TO_COMPARE_CONSUMPTION,
    yearOptions: [] as number[],
    ownerDataCup: initialOwnerDataCup,
  });

  useEffect(() => {
    if (selectedTab === "download" && scrollPosition) {
      (tableElementRef.current as HTMLDivElement).scrollTop = scrollPosition;
    }
  }, [scrollPosition, selectedTab]);

  const ownerDataCups = useMemo(() => {
    return ownerData?.cups || [];
  }, [ownerData?.cups]);

  const isLoadingAgentData = useMemo(
    () => Object.values(agentDataLoadingState).some((state) => state),
    [agentDataLoadingState]
  );

  if (isLoadingAgentData) {
    return (
      <LoadingComponentComponent
        isLoading={true}
        opacity={0.5}
        position={"absolute"}
        width={"100%"}
        height={"100%"}
      />
    );
  }

  return (
    <>
      <ConsumptionsHeader
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
      <Container component="article">
        {selectedTab === "download" && (
          <ConsumptionsDonwload
            selectedDownloadCups={selectedDownloadCups}
            setSelectedDownloadCups={setSelectedDownloadCups}
            setScrollPosition={setScrollPosition}
            selectedDownloadData={selectedDownloadData}
            setSelectedDownloadData={setSelectedDownloadData}
            tableElementRef={tableElementRef}
          />
        )}
        {selectedTab === "hourlyConsumption" && (
          <ConsumptionsHourly
            ownerDataCups={ownerDataCups}
            hourlyConsumptionsList={hourlyConsumptionsList}
            setHourlyConsumptionsList={setHourlyConsumptionsList}
            searchParams={searchPeriodParams}
            setSearchParams={setSearchPeriodParams}
            selectedDataPeriod={selectedDataPeriodHourly}
            setSelectedDataPeriod={setSelectedDataPeriodHourly}
          />
        )}
        {selectedTab === "periodConsumption" && (
          <ConsumptionsPeriod
            ownerDataCups={ownerDataCups}
            userData={userData}
            consumptionsList={consumptionsListPeriod}
            setConsumptionsList={setConsumptionsListPeriod}
            consumptionsPeriodInformation={consumptionsPeriodInformation}
            setConsumptionsPeriodInformation={setConsumptionsPeriodInformation}
            searchParams={searchPeriodParams}
            setSearchParams={setSearchPeriodParams}
            selectedDataPeriod={selectedDataPeriodPeriod}
            setSelectedDataPeriod={setSelectedDataPeriodPeriod}
          />
        )}
        {selectedTab === "comparativeConsumption" && (
          <ConsumptionsComparative
            ownerDataCups={ownerDataCups}
            consumptionsList={consumptionsListComparative}
            setConsumptionsList={setConsumptionsListComparative}
            selectedDataPeriod={selectedDataPeriodComparative}
            setSelectedDataPeriod={setSelectedDataPeriodComparative}
            comparisonState={comparisonState}
            setComparisonState={setComparisonState}
          />
        )}
      </Container>
    </>
  );
};

export default Consumptions;
